/**
* @summary - License Create and Edit Form
* @author Yuvasri S
* @since 2024-12-17
*/
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Box, Grid } from "@mui/material";
import utils from "../../utility/utilities";
import { getFacilityId } from "../../utility/tokenUtility";
import CustomDialog from "../commoncomponents/CustomDialog";
import CustomTextField from "../commoncomponents/CustomTextField";
import CustomTextFieldWithSearch from "../commoncomponents/CustomTextFieldWithSearch";
import { DefaultCard } from "../../assets/styled-components/card";
import CustomSelectInput from "../commoncomponents/CustomSelectInput";
import CustomDatePicker from "../commoncomponents/CustomDatePicker";
import PageTitle from "../commoncomponents/PageTitle";
import LicenseCertificateRepository from '../../api/repositories/licenseCertificateRepository';
import moment from "moment";
import { useLocation } from "react-router-dom";


const LicenseCertificateForm = forwardRef((props, ref) => {
    const location = useLocation();
    const editMode = location?.state?.editmode;
    const viewMode = location?.state?.viewmode;
    const facilityId = getFacilityId('facilityId');
    const { onLicenseCategoryChange } = props;
    const [serviceOptions, setServiceOptions] = useState([]);
    const [licensecategoryOptions, setLicensecategoryOptions] = useState([]);
    const [hospitalNameOptions, setHospitalNameOptions] = useState([]);
    const [licenseTypecodeOptions, setLicenseTypeCodeOptions] = useState([]);
    const currentDate = utils.getCurrentDateFormatted("DD-MM-YYYY");
    const [openAlertDialog, setOpenAlertDialog] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');

    const active = {
        label: "Active",
        value: "Active",
        id: 1,
    };
    const inActive = {
        label: "Inactive",
        value: "Inactive",
        id: 2,
    };
    const expried = {
        label: "Expired",
        value: "Expired",
        id: 3,
    };
    const statusOptions = editMode ? [active, inActive] : viewMode ? [active, inActive, expried] : [active, inActive];

    const [formState, setFormState] = useState({
        service: '',
        licenseNo: '',
        licenseCategory: '',
        hospitalName: '',
        licenseTypeCode: '',
        licenseTypeCodeDesc: '',
        status: active,
        classGrade: '',
        inspectedBy: '',
        contactPerson: '',
        inspectionSubmittedDate: '',
        inspectedConductOn: '',
        nextInspectionDate: '',
        expiryDate: '',
        issuingBody: '',
        issueDate: '',
        remarks: '',
        maslicensecertstatusId: '',
        reason: '',

    });

    const [errors, setErrors] = useState({});
    const [isDirty, setIsDirty] = useState(false); //unsaved changes
    const [hospital, setHospital] = useState('');
    const formatDateForDisplay = (date) => {
        if (!date) {
            return '';
        }

        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;

    };
    const handleCloseAlertDialog = () => {
        setOpenAlertDialog(false);
    };

    const formatDateForBackend = (date) => {
        let dayin = !!date ? new Date(date) : null;

        if (dayin) {
            if (isNaN(dayin.getTime())) {
                console.error('Invalid Date:', date);
            } else {

                let dateformat = moment(dayin).unix() * 1000

                let Monthyear = moment(dateformat).format('YYYY-MM-DD HH:mm:ss');

                return Monthyear

            }
        }
    };
    const validateDate = (date, comparisonDate, comparisonType, granularity = 'day') => {
        switch (comparisonType) {
            case 'isAfter':
                return date.isAfter(comparisonDate, granularity);
            case 'isBefore':
                return date.isBefore(comparisonDate, granularity);
            case 'isSame':
                return date.isSame(comparisonDate, granularity);
            case 'isSameOrAfter':
                return date.isSameOrAfter(comparisonDate, granularity);
            case 'isSameOrBefore':
                return date.isSameOrBefore(comparisonDate, granularity);
            default:
                return false;
        }
    };

    const handleDateChange = (field, newDate) => {
        setIsDirty(true);
        if (newDate) {
            const momentDate = moment(newDate, 'DD/MM/YYYY', true);
            const inspectedMoment = moment(formState.inspectedConductOn, 'YYYY-MM-DDTHH:mm:ss', true);
            const submittedMoment = moment(formState.inspectionSubmittedDate, 'YYYY-MM-DDTHH:mm:ss', true);
            const nextInspectionMoment = moment(formState.nextInspectionDate, 'YYYY-MM-DDTHH:mm:ss', true);
            const currentMoment = moment();

            if (!momentDate.isValid()) return;

            const formattedDate = momentDate.format('YYYY-MM-DDTHH:mm:ss');
            let newErrors = { ...errors };

            const setAlert = (message, field) => {
                newErrors[field] = true;
                setAlertMessage(message);
                setOpenAlertDialog(true);
            };

            switch (field) {
                case 'inspectionSubmittedDate':
                    if (validateDate(momentDate, currentMoment, 'isAfter')) {
                        setAlert('Submitted date for Inspection cannot be in the future.', 'inspectionSubmittedDate');
                    } else if (formState.inspectedConductOn && validateDate(momentDate, inspectedMoment, 'isAfter')) {
                        setAlert('Submitted date for Inspection cannot be after the Inspection Conducted date.', 'inspectionSubmittedDate');
                    } else {
                        newErrors.inspectionSubmittedDate = false;
                    }

                    setFormState((prev) => ({ ...prev, inspectionSubmittedDate: formattedDate }));
                    break;

                case 'inspectedConductOn':
                    if (validateDate(momentDate, currentMoment, 'isAfter')) {
                        setAlert('Inspection Conducted date cannot be in the future.', 'inspectedConductOn');
                    } else if (formState.inspectionSubmittedDate && validateDate(momentDate, submittedMoment, 'isBefore')) {
                        setAlert('Inspection Conducted date must not be before the Submitted date for Inspection.', 'inspectedConductOn');
                    } else if (formState.nextInspectionDate && validateDate(momentDate, nextInspectionMoment, 'isSame')) {
                        setAlert('Inspection Conducted date should not be the same as the Next Inspection Date.', 'inspectedConductOn');
                    } else if (formState.nextInspectionDate && validateDate(momentDate, nextInspectionMoment, 'isAfter')) {
                        setAlert('Inspection Conducted date should not be after the Next Inspection Date.', 'inspectedConductOn');
                    } else {
                        newErrors.inspectedConductOn = false;
                    }

                    setFormState((prev) => ({ ...prev, inspectedConductOn: formattedDate }));
                    break;

                case 'nextInspectionDate':
                    if (formState.inspectedConductOn && validateDate(momentDate, inspectedMoment, 'isSameOrBefore')) {
                        setAlert('Next Inspection Date must be after the Inspection Conducted date.', 'nextInspectionDate');
                    } else {
                        newErrors.nextInspectionDate = false;
                    }

                    setFormState((prev) => ({ ...prev, nextInspectionDate: formattedDate }));
                    break;

                case 'expiryDate':
                    // past date not allowed
                    if (validateDate(momentDate, currentMoment, 'isBefore')) {
                        setAlert('Expiry date cannot be in the past.', 'expiryDate');
                        setFormState((prev) => ({
                            ...prev,
                            expiryDate: ''
                        }));
                    } else {
                        // today or future → Active
                        setFormState((prev) => ({
                            ...prev,
                            expiryDate: formattedDate,
                            formattedExpiryDate: momentDate.format('DD-MM-YYYY'),
                            status: { id: 1, label: "Active", value: "Active" } // ✅ set Active
                        }));
                        newErrors.expiryDate = false;
                    }
                    break;


                case 'issueDate':
                    setFormState((prev) => ({
                        ...prev,
                        issueDate: formattedDate
                    }));
                    break;

                default:
                    break;
            }
            setErrors(newErrors);
        }
    };

    useEffect(() => {
        if (editMode) return; // Skip execution if in edit mode
        if (viewMode) return;
        const getLov = async () => {
            try {
                const services = await LicenseCertificateRepository.fetchServiceOptions();

                setServiceOptions(services);
                if (services.length > 0) {
                    handleOnChange(services[1], "service");
                }
                const calibrationCategorys = await LicenseCertificateRepository.fetchCalibrationCategoryOptions();
                setLicensecategoryOptions(calibrationCategorys);
                if (calibrationCategorys.length > 0) {
                    handleOnChange(calibrationCategorys[0], "licenseCategory");
                }

                // const hospitalName = await LicenseCertificateRepository.fetchHospitalName();
                // setHospitalNameOptions(hospitalName);

            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        getLov();
    }, []);

    useEffect(() => {
        const fetchCalibrationTypeCodeOptions = async () => {
            if (formState?.service?.id && formState?.licenseCategory?.id) {
                try {
                    const licenseTypeCode = await LicenseCertificateRepository.fetchcalibrationTypeCodeOptions(
                        formState?.service.id,
                        formState.licenseCategory.id
                    );
                    setLicenseTypeCodeOptions(licenseTypeCode);
                } catch (error) {
                    console.error("Error fetching calibration type code options:", error);
                }
            } else {
                setLicenseTypeCodeOptions([]); // Clear options if dependencies are not set
            }
        };

        fetchCalibrationTypeCodeOptions();
    }, [formState?.licenseCategory, formState?.service]);


    useEffect(() => {

        if (editMode) {
            const fetchCalibrationTypeCodeOptions = async () => {
                try {
                    const licenseTypeCode = await LicenseCertificateRepository.fetchcalibrationTypeCodeOptions(
                        location?.state?.serviceId,
                        location?.state?.categoryId
                    );
                    setLicenseTypeCodeOptions(licenseTypeCode);

                } catch (error) {
                    console.error("Error fetching calibration type code options:", error);
                }

            };

            fetchCalibrationTypeCodeOptions();
        }
    }, [editMode]);

    useEffect(() => {
        if (props.onLicenseCategoryChange) {
            props.onLicenseCategoryChange(formState.licenseCategory);
        }
    }, [formState.licenseCategory]);


    useEffect(() => {
        if (location?.state) {
            const { serviceId, service, licnno, categoryprimaryid, categoryName, licenseId, licntypecode,
                licntypevalue, licnclassgrade, isactive, isactiveformate, licninspectedby, licncontactperson, licnsubmitteddate, licninspectiondate,
                licnnextinspectiondate, licnexpirydateformat, licnexpirydate, licnissuingbody, licnissuedate, licnremarks, licncategorydesc, isactiveid, reason } = location.state

            const serviceVal = { id: serviceId, label: service, value: service }
            const categoryVal = { primaryId: categoryprimaryid, label: categoryName, value: categoryName }
            const typeCodeVal = { id: licenseId, label: licntypecode, value: licntypevalue }
            const statusVal = { id: isactiveid, label: isactiveformate, value: isactiveformate }
            // const hospitalVal = { label: licncategorydesc, value: licncategorydesc }
            const hospitalVal = licncategorydesc
            setFormState({
                service: serviceVal, licenseNo: licnno, licenseCategory: categoryVal,
                licenseTypeCode: typeCodeVal, licenseTypeCodeDesc: licntypevalue, classGrade: licnclassgrade,
                status: statusVal, inspectedBy: licninspectedby, contactPerson: licncontactperson, inspectionSubmittedDate: licnsubmitteddate,
                inspectedConductOn: licninspectiondate, nextInspectionDate: licnnextinspectiondate, expiryDate: licnexpirydate,
                issuingBody: licnissuingbody, issueDate: licnissuedate, remarks: licnremarks, hospitalName: hospitalVal, maslicensecertstatusId: isactiveid,
                reason: reason
            })
            setHospital(categoryName);
        }

    }, [location?.state])



    const handleNumericValue = (value) => {
        const numericValue = value.replace(/[^0-9]/g, "");
        return numericValue.slice(0, 13);
    };

    const handleOnChange = (values, name) => {
        let processedValue = values || "";

        console.log(values, name, "values,name");
        // Handle numeric value processing
        if (name === "telephoneNo" || name === "faxNo") {
            processedValue = handleNumericValue(values);
        }
        if (name === "licenseCategory" && values.value === "Hospital") {
            console.log(true, "==>");
            console.log(localStorage.getItem('FacilityName'), "localStorage.getItem('FacilityName')");
            setFormState((prev) => ({ ...prev, hospitalName: localStorage.getItem('FacilityName') || '' }));
        } else if (name === "licenseCategory" && values.value !== "Hospital") {
            console.log("false");

            setFormState((prev) => ({ ...prev, hospitalName: '' }));
        }

        setFormState((prev) => {
            const updatedState = { ...prev, [name]: processedValue };

            // Clear related fields when service is changed
            if (name === "service") {
                updatedState.licenseCategory = ""; // Clear licenseCategory
                updatedState.licenseTypeCode = ""; // Clear licenseTypeCode
                updatedState.licenseTypeCodeDesc = ""; // Clear licenseTypeCodeDesc
                updatedState.status = {
                    label: "Active",
                    value: "Active",
                    id: 1,
                };
            }
            if (name === "status") {
                if (formState?.status?.value !== "Inactive") {
                    updatedState.reason = "";
                    setErrors((prev) => ({ ...prev, reason: false }));

                }
            }



            // Clear related fields when licenseCategory is changed
            if (name === "licenseCategory") {
                updatedState.licenseTypeCode = ""; // Clear licenseTypeCode
                updatedState.licenseTypeCodeDesc = ""; // Clear licenseTypeCodeDesc
                //updatedState.hospitalName = "";
                if (onLicenseCategoryChange) {
                    onLicenseCategoryChange(processedValue);
                    setHospital(processedValue); // Update hospital logic if needed
                }
            }
            if (name === "licenseTypeCode") {
                updatedState.licenseTypeCodeDesc = processedValue.value || ""; // Clear licenseTypeCodeDesc
            }

            // Set status to active if other fields are being updated
            // if (name !== "status" && prev.status?.value !== "Active") {
            // if (name !== "status" ) {


            //     };
            // }

            return updatedState;
        });

        setIsDirty(true);

        // Validation logic
        setErrors((prev) => {
            let errorMessage = null;

            // Required field validation for specific fields
            if (
                ["service", "licenseNo", "licenseTypeCode", "licenseCategory", "expiryDate"].includes(name) &&
                !processedValue
            ) {
                errorMessage = `${name === "service"
                    ? "Service"
                    : name === "licenseNo"
                        ? "License No."
                        : name === "licenseTypeCode"
                            ? "License Type Code"
                            : name === "licenseCategory"
                                ? "License Category"
                                : name === "expiryDate"
                                    ? "Expiry Date"
                                    : ""
                    } is required.`;
            }

            // Additional validation for hospitalName when licenseCategory.value is 'Hospital'
            // if (
            //     name === "licenseCategory" &&
            //     values?.value === "Hospital" &&
            //     !formState?.hospitalName
            // )
            // {
            //     return {
            //         ...prev,
            //         hospitalName: "Hospital Name is required.",
            //     };
            // }

            // If hospitalName is being updated, validate its requirement
            // if (name === "hospitalName" && !processedValue) {
            //     errorMessage = "Hospital Name is required.";
            // }

            return { ...prev, [name]: errorMessage };
        });
    };

    const validateFields = async () => {
        const requiredFields = ['service', 'licenseNo', 'licenseTypeCode', 'licenseCategory', 'expiryDate'];
        const newErrors = {};

        // Validate required fields
        requiredFields.forEach((field) => {
            if (
                !formState[field] ||
                (field === 'licenseTypeCode' && !formState[field].label) ||
                (field === 'licenseCategory' && !formState[field].value) ||
                (field === 'expiryDate' && !formState[field])
            ) {
                newErrors[field] = `${field === 'service'
                    ? 'Service'
                    : field === 'licenseNo'
                        ? 'License No.'
                        : field === 'licenseTypeCode'
                            ? 'License Type Code'
                            : field === 'licenseCategory'
                                ? 'License Category'
                                : field === 'expiryDate'
                                    ? 'Expiry Date'
                                    : ''
                    } is required.`;
            }
        });

        if (formState?.status?.value === 'Inactive' && !formState.reason) {

            newErrors.reason = 'Reason is required when status is Inactive.';
        }

        // // Additional validation for hospitalName when licenseCategory.value === 'Hospital'
        // if (formState?.licenseCategory?.value === 'Hospital' && !formState.hospitalName?.label) {
        //     newErrors.hospitalName = 'Hospital Name is required.';
        // }

        // Validate inspectedConductOn
        if (formState.inspectedConductOn) {
            const inspectedConductMoment = moment(formState.inspectedConductOn, 'YYYY-MM-DDTHH:mm:ss', true);
            const currentMoment = moment();
            const nextInspectionMoment = moment(formState.nextInspectionDate, 'YYYY-MM-DDTHH:mm:ss', true);
            const inspectionSubmittedMoment = moment(formState.inspectionSubmittedDate, 'YYYY-MM-DDTHH:mm:ss', true);

            if (inspectedConductMoment.isAfter(currentMoment, 'day')) {
                newErrors.inspectedConductOn = 'Inspection Conducted date cannot be in the future.';
            } else if (
                formState.nextInspectionDate &&
                nextInspectionMoment.isValid() &&
                inspectedConductMoment.isSameOrAfter(nextInspectionMoment, 'day')
            ) {
                newErrors.inspectedConductOn = 'Inspection Conducted date cannot be on or after the Next Inspection Date.';
            } else if (
                formState.inspectionSubmittedDate &&
                inspectionSubmittedMoment.isValid() &&
                inspectedConductMoment.isBefore(inspectionSubmittedMoment, 'day')
            ) {
                newErrors.inspectedConductOn =
                    'Inspection Conducted date must not be before the Submitted date for Inspection.';
            }
        }

        // Validate nextInspectionDate
        if (formState.nextInspectionDate) {
            const nextInspectionMoment = moment(formState.nextInspectionDate, 'YYYY-MM-DDTHH:mm:ss', true);
            const inspectedConductMoment = moment(formState.inspectedConductOn, 'YYYY-MM-DDTHH:mm:ss', true);

            if (!nextInspectionMoment.isValid()) {
                newErrors.nextInspectionDate = 'Invalid Next Inspection Date.';
            } else if (
                formState.inspectedConductOn &&
                inspectedConductMoment.isValid() &&
                nextInspectionMoment.isSameOrBefore(inspectedConductMoment, 'day')
            ) {
                newErrors.nextInspectionDate = 'Next Inspection Date must be after the Inspection Conducted date.';
            }
        }

        // Validate inspectionSubmittedDate
        if (formState.inspectionSubmittedDate) {
            const inspectionSubmittedMoment = moment(formState.inspectionSubmittedDate, 'YYYY-MM-DDTHH:mm:ss', true);
            const inspectedConductMoment = moment(formState.inspectedConductOn, 'YYYY-MM-DDTHH:mm:ss', true);
            const currentMoment = moment();

            if (inspectionSubmittedMoment.isAfter(currentMoment, 'day')) {
                newErrors.inspectionSubmittedDate = 'Submitted date for Inspection cannot be in the future.';
            } else if (
                formState.inspectedConductOn &&
                inspectedConductMoment.isValid() &&
                inspectionSubmittedMoment.isAfter(inspectedConductMoment, 'day')
            ) {
                newErrors.inspectionSubmittedDate =
                    'Submitted date for Inspection must not be after the Inspection Conducted date.';
            }
        }
        // Finalize errors and validation result
        if (Object.values(newErrors).some((error) => error)) {

            setErrors(newErrors);
            return false; // Validation failed
        } else {

            setErrors({}); // Clear all errors if validation passed
        }

        return true; // Validation passed
    };


    const handleSubmit = async () => {
        // Call the validateFields function to validate all fields
        const isValid = await validateFields();
        // If validation fails, return early
        if (!isValid) {

            return;
        }
        const createPayload = {
            licnno: formState.licenseNo,
            licnclassgrade: formState.classGrade,
            isactive: formState.status.id === 1 ? true : false,
            licninspectedby: formState.inspectedBy,
            licncontactperson: formState.contactPerson,
            licnsubmitteddate: formatDateForBackend(formState.inspectionSubmittedDate),
            licninspectiondate: formatDateForBackend(formState.inspectedConductOn),
            licnnextinspectiondate: formatDateForBackend(formState.nextInspectionDate),
            licnexpirydate: formatDateForBackend(formState.expiryDate),
            licnissuingbody: formState.issuingBody,
            licnissuedate: formatDateForBackend(formState.issueDate),
            licnremarks: formState.remarks,
            licncategorydesc: formState.licenseCategory.value,
            masservicedetailsId: formState.service.id,
            facilityid: facilityId,
            isdeleted: false,
            maslicensetypecodedetailsId: formState.licenseTypeCode.id,
            maslicensecertcategoryId: formState?.licenseCategory.primaryId,
            id: location?.state?.id || "",
            maslicensecertstatusId: formState.status.id || formState?.maslicensecertstatusId,
            licncategorydesc: formState.hospitalName || '',
            reason: formState.reason || '',
        };

        // API call to create calibration details
        const createResponse = editMode ? await LicenseCertificateRepository.updateLicenseDetails(location?.state?.id, createPayload) : await LicenseCertificateRepository.createLicenseDetails(createPayload);
        setIsDirty(false);
        return createResponse;

    };
    useImperativeHandle(ref, () => ({
        handleSubmit,
        validateFields,
        hasUnsavedChanges: () => isDirty,
        hospital
    }));
    useEffect(() => {
        if (props.onServiceChange) {
            props.onServiceChange(formState.service);
        }
    }, [formState.service]);

    console.log(formState, "Formstate");


    return (
        <>
            <PageTitle
                title={location?.state?.title}
            />

            <Box mt={{ xs: 1, md: 2 }}>

                <DefaultCard sx={{ height: "100%" }}>
                    <form onSubmit={handleSubmit} style={{ position: "relative" }}>
                        <Box mb={2}>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={4}>
                                    <CustomSelectInput
                                        disableClearable={true}
                                        label="Service"
                                        isMandatoryField={true}
                                        value={formState.service}
                                        onChange={(value) => handleOnChange(value, "service")}
                                        options={serviceOptions}
                                        id="service"
                                        name="service"
                                        error={!!errors.service}
                                        errorMessage={errors.service}
                                        autoFocus
                                        disabled={editMode || viewMode}
                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomTextField
                                        label="License No."
                                        id="License No."
                                        maxlength={60}
                                        value={formState.licenseNo}
                                        onChange={(e) => handleOnChange(e, "licenseNo")}
                                        isMandatoryField={true}
                                        error={!!errors.licenseNo}
                                        errorMessage={errors.licenseNo}
                                        disabled={editMode || viewMode}
                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomSelectInput
                                        label="License Category"
                                        id="License-category"
                                        name="licenseCategory"
                                        onChange={(value) => handleOnChange(value, "licenseCategory")}
                                        options={licensecategoryOptions}
                                        isMandatoryField={true}
                                        value={formState.licenseCategory || ""}
                                        error={!!errors.licenseCategory}
                                        errorMessage={errors.licenseCategory}
                                        disabled={editMode || viewMode}
                                        disableClearable={true}
                                    />
                                </Grid>


                                {formState.licenseCategory.value === 'Hospital' && (
                                    // <Grid item xs={6} md={4}>
                                    //     <CustomSelectInput
                                    //         label="Hospital Name"
                                    //         id="hospital-name"
                                    //         value={formState.hospitalName}
                                    //         isMandatoryField={true}
                                    //         error={!!errors.hospitalName}
                                    //         errorMessage={errors.hospitalName}
                                    //         onChange={(value) => handleOnChange(value, "hospitalName")}
                                    //         options={hospitalNameOptions}
                                    //         disabled={editMode || viewMode}
                                    //     />
                                    // </Grid>
                                    <Grid item xs={6} md={4}>
                                        <CustomTextField
                                            label="Hospital Name"
                                            id="hospital-name"
                                            onChange={handleOnChange}
                                            value={formState.hospitalName}
                                            // error={!!errors.inspectedBy}
                                            // errormessages={errors.inspectedBy}
                                            disabled={true}

                                        />
                                    </Grid>
                                )}
                                <Grid item xs={6} md={4}>
                                    <CustomTextFieldWithSearch
                                        label="License Type Code"
                                        id="License Type Code"
                                        name="licenseTypeCode"
                                        value={formState.licenseTypeCode}
                                        options={licenseTypecodeOptions.map(option => ({
                                            label: option.label,
                                            value: option.value,
                                            id: option.id
                                        }))}
                                        onChange={handleOnChange}
                                        isMandatoryField={true}
                                        error={!!errors.licenseTypeCode}
                                        errorMessage={errors.licenseTypeCode}
                                        disabled={viewMode}
                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomTextField
                                        label="License Type Code Description"
                                        id="licenseTypecodeDesc"
                                        placeholder={""}
                                        name="licenseTypecodeDesc"
                                        disabled
                                        value={formState.licenseTypeCodeDesc}
                                        isRefilByOtherField={true}
                                    />


                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomTextField
                                        label="Class/Grade"
                                        id="class-grade"
                                        maxlength={60}
                                        onChange={(e) => handleOnChange(e, "classGrade")}
                                        value={formState.classGrade}
                                        error={!!errors.classGrade}
                                        errormessages={errors.classGrade}
                                        disabled={viewMode}
                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>

                                    <CustomSelectInput
                                        disableClearable={true}
                                        label="Status"
                                        id="status"
                                        name="status"
                                        value={formState.status}
                                        onChange={handleOnChange}
                                        options={statusOptions}
                                        defaultOption={statusOptions[0]}
                                        disabled={viewMode}
                                    />

                                </Grid>
                                {(formState.status.value === 'Inactive') && (
                                    < Grid item xs={6} md={4}>
                                        <CustomTextField
                                            label="Reason"
                                            id="reason"
                                            name="reason"
                                            value={formState.reason}
                                            onChange={handleOnChange}
                                            error={!!errors.reason}
                                            helperText={errors.reason}
                                            isMandatoryField={formState.status.value === 'Inactive'}
                                            disabled={viewMode} />
                                    </Grid>
                                )}

                                <Grid item xs={6} md={4}>
                                    <CustomTextField
                                        label="Inspected By"
                                        id="inspected-by"
                                        onChange={(e) => handleOnChange(e, "inspectedBy")}
                                        value={formState.inspectedBy}
                                        error={!!errors.inspectedBy}
                                        errormessages={errors.inspectedBy}
                                        disabled={viewMode}

                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomTextField
                                        label="Contact Person"
                                        id="contact-person"
                                        onChange={(e) => handleOnChange(e, "contactPerson")}
                                        value={formState.contactPerson}
                                        error={!!errors.contactPerson}
                                        errormessages={errors.contactPerson}
                                        disabled={viewMode}

                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomDatePicker
                                        label="Date Submitted for Inspection"
                                        id="date-for-inspection"
                                        value={formatDateForDisplay(formState.inspectionSubmittedDate)}
                                        onDateChange={(newDate) => handleDateChange('inspectionSubmittedDate', newDate)}
                                        error={!!errors.inspectionSubmittedDate}
                                        errormessages={errors.inspectionSubmittedDate ? 'Submitted date cannot be in the future.' : ''}
                                        maxDate={currentDate}
                                        disabled={viewMode}
                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomDatePicker
                                        label="Inspection Conducted on"
                                        id="inspection-conducted"
                                        value={formatDateForDisplay(formState.inspectedConductOn)}
                                        onDateChange={(newDate) => handleDateChange('inspectedConductOn', newDate)}
                                        error={!!errors.inspectedConductOn}
                                        errormessages={
                                            errors.inspectedConductOn
                                                ? 'Inspection Conducted date cannot be in the future or on/after the Next Inspection Date.'
                                                : ''
                                        }
                                        maxDate={currentDate}
                                        minDate={formState.inspectionSubmittedDate ? moment(formState.inspectionSubmittedDate).add(1, 'day').format('YYYY-MM-DD') : null}
                                        disabled={viewMode}
                                    />
                                </Grid>

                                <Grid item xs={6} md={4}>
                                    <CustomDatePicker
                                        label="Next Inspection Date"
                                        id="next-inspection-date"
                                        value={formatDateForDisplay(formState.nextInspectionDate)}
                                        onDateChange={(newDate) => handleDateChange('nextInspectionDate', newDate)}
                                        error={!!errors.nextInspectionDate}
                                        errormessages={
                                            errors.nextInspectionDate
                                                ? 'Next Inspection Date must be after the Inspection Conducted date.'
                                                : ''
                                        }
                                        minDate={formState.inspectedConductOn ? moment(formState.inspectedConductOn).add(1, 'day').format('YYYY-MM-DD') : null}
                                        disabled={viewMode}
                                    />
                                </Grid>

                                <Grid item xs={6} md={4}>
                                    <CustomDatePicker
                                        label="Expiry Date"
                                        id="expiry-date"
                                        value={formatDateForDisplay(formState.expiryDate)}
                                        onDateChange={(newDate) => handleDateChange('expiryDate', newDate)}
                                        minimumDate={moment().format('DD/MM/YYYY')} // Set the minimum date as today
                                        isMandatoryField={true}
                                        error={!!errors.expiryDate}
                                        errorMessage={errors.expiryDate}
                                        disabled={viewMode}
                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomTextField
                                        label="Issuing Body	"
                                        id="issuing-body"
                                        onChange={(e) => handleOnChange(e, "issuingBody")}
                                        value={formState.issuingBody}
                                        disabled={viewMode}

                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomDatePicker
                                        label="Issue Date"
                                        id="issue-date"
                                        value={formatDateForDisplay(formState.issueDate)}
                                        onDateChange={(newDate) => handleDateChange('issueDate', newDate)}
                                        error={!!errors.issueDate}
                                        errormessages={errors.issueDate}
                                        disabled={viewMode} />

                                </Grid>
                                <Grid item xs={6} md={8}>
                                    <CustomTextField
                                        label="Remarks"
                                        id="remarks"
                                        name="remarks"
                                        value={formState.remarks}
                                        onChange={(e) => handleOnChange(e, "remarks")}
                                        error={!!errors.remarks}
                                        errormessages={errors.remarks}
                                        disabled={viewMode} />
                                </Grid>
                            </Grid>

                        </Box>
                        <CustomDialog
                            open={openAlertDialog}
                            onClose={handleCloseAlertDialog}
                            onConfirm={handleCloseAlertDialog}
                            content={alertMessage}
                            confirmBtn="Ok"
                        />
                    </form>
                </DefaultCard>

            </Box >
        </>
    )
});
export default LicenseCertificateForm;